import { useTranslator } from '@Localization';
import { useInjection } from 'inversify-react';
import { checkAll, IPermissionCheckResult } from '../../../modules/permissions/permissionsHelpers';
import { ICurrentWorkspaceStore } from '../../../modules/workspaces/currentWorkspace/CurrentWorkspace.store';
import { ProjectStatus } from '../projects/projects.model';
import { Plan } from '../user/user.store';
import { WorkspaceRole } from '../workspaces/workspaces.store';

interface IProjectDetailsPermissions {
  canEditAnnotationTypeColor(): IPermissionCheckResult;
  canEditProject(projectStatus: ProjectStatus): IPermissionCheckResult;
  canUpdateImageSet(): IPermissionCheckResult;
  canSeeAnnotationAuthors(): IPermissionCheckResult;
  canBatchAnswer(): IPermissionCheckResult;
  canManageUsers(): IPermissionCheckResult;
  canUnlinkDataset(): IPermissionCheckResult;
  canViewDataset(): IPermissionCheckResult;
  canEditDatasetOrder(): IPermissionCheckResult;
  canUpdateImageAssignmentPolicy(): IPermissionCheckResult;
  canExport(): IPermissionCheckResult;
  canViewActivityLog(): IPermissionCheckResult;
  canViewStatistics(): IPermissionCheckResult;
  canViewWorkers(): IPermissionCheckResult;
  canAddQuestion(): IPermissionCheckResult;
  canImportAnnotations(): IPermissionCheckResult;
}

export const useProjectDetailsPermissions = (): IProjectDetailsPermissions => {
  const currentWorkspaceStore = useInjection(ICurrentWorkspaceStore.$);
  const currentWorkspace = currentWorkspaceStore.currentWorkspace;
  const { t } = useTranslator();

  const standardPlans = [Plan.Admin, Plan.Annotation, Plan.Training, Plan.Free];
  const standardRoles = [WorkspaceRole.Owner, WorkspaceRole.Manager, WorkspaceRole.Developer, WorkspaceRole.Trainer];

  const canEditProject = (projectStatus: ProjectStatus): IPermissionCheckResult => {
    return projectStatus === ProjectStatus.Published
      ? checkAll(standardPlans, [WorkspaceRole.Owner, WorkspaceRole.Manager], currentWorkspace, t)
      : checkAll(standardPlans, standardRoles, currentWorkspace, t);
  };

  const canEditAnnotationTypeColor = (): IPermissionCheckResult => {
    return checkAll(standardPlans, standardRoles, currentWorkspace, t);
  };

  const canUpdateImageSet = (): IPermissionCheckResult => {
    return checkAll(standardPlans, standardRoles, currentWorkspace, t);
  };

  const canSeeAnnotationAuthors = (): IPermissionCheckResult => {
    return checkAll(standardPlans, standardRoles, currentWorkspace, t);
  };

  const canBatchAnswer = (): IPermissionCheckResult => {
    return checkAll(standardPlans, standardRoles, currentWorkspace, t);
  };

  const canManageUsers = (): IPermissionCheckResult => {
    return checkAll(standardPlans, standardRoles, currentWorkspace, t);
  };
  const canUnlinkDataset = (): IPermissionCheckResult => {
    return checkAll(standardPlans, standardRoles, currentWorkspace, t);
  };

  const canViewDataset = (): IPermissionCheckResult => {
    return checkAll([...standardPlans, Plan.Inactive], standardRoles, currentWorkspace, t);
  };

  const canEditDatasetOrder = (): IPermissionCheckResult => {
    return checkAll(standardPlans, [WorkspaceRole.Owner, WorkspaceRole.Manager], currentWorkspace, t);
  };

  const canUpdateImageAssignmentPolicy = (): IPermissionCheckResult => {
    return checkAll(standardPlans, [WorkspaceRole.Owner, WorkspaceRole.Manager], currentWorkspace, t);
  };

  const canExport = (): IPermissionCheckResult => {
    return checkAll([...standardPlans, Plan.Inactive], standardRoles, currentWorkspace, t);
  };

  const canViewActivityLog = (): IPermissionCheckResult => {
    return checkAll([...standardPlans, Plan.Inactive], standardRoles, currentWorkspace, t);
  };

  const canViewStatistics = (): IPermissionCheckResult => {
    return checkAll([...standardPlans, Plan.Inactive], standardRoles, currentWorkspace, t);
  };

  const canViewWorkers = (): IPermissionCheckResult => {
    return checkAll([...standardPlans, Plan.Inactive], standardRoles, currentWorkspace, t);
  };

  const canAddQuestion = (): IPermissionCheckResult => {
    return checkAll(standardPlans, standardRoles, currentWorkspace, t);
  };

  const canImportAnnotations = (): IPermissionCheckResult => {
    return checkAll(
      [Plan.Admin, Plan.Training, Plan.Annotation, Plan.Free],
      [WorkspaceRole.Owner, WorkspaceRole.Manager, WorkspaceRole.Developer, WorkspaceRole.Trainer, WorkspaceRole.Collaborator],
      currentWorkspace,
      t
    );
  };

  return {
    canEditAnnotationTypeColor,
    canEditProject,
    canUpdateImageSet,
    canSeeAnnotationAuthors,
    canBatchAnswer,
    canManageUsers,
    canUnlinkDataset,
    canViewDataset,
    canEditDatasetOrder,
    canUpdateImageAssignmentPolicy,
    canExport,
    canViewActivityLog,
    canViewStatistics,
    canViewWorkers,
    canAddQuestion,
    canImportAnnotations,
  };
};
